// redux/slices/recurringBreakSlice.js
import { createSlice, createAsyncThunk, createSelector } from "@reduxjs/toolkit";
import { resetStore } from "./resetStore";
import { apiRequest } from "../../Utils/apiClient";
import { clearJwtToken } from "../../Utils/authStorage";

const getError = (error, fallback) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  fallback;

const handleAuthError = async (error) => {
  const status = error?.response?.status;
  if (status === 401 || status === 403) {
    try {
      await clearJwtToken();
    } catch {}
  }
};

// Исключение в едином виде: дата всегда 'YYYY-MM-DD', id правила — число
const normalizeException = (ex, uid) => ({
  id: ex?.id ?? null,
  uid: String(uid ?? ex?.uid ?? ""),
  recurringBreakId: Number(ex?.recurringBreakId),
  dateOfBreak: String(ex?.dateOfBreak ?? "").slice(0, 10),
});

const isSameException = (a, recurringBreakId, dateOfBreak) =>
  String(a.recurringBreakId) === String(recurringBreakId) &&
  String(a.dateOfBreak) === String(dateOfBreak).slice(0, 10);

export const addRecurringBreak = createAsyncThunk(
  "recurringBreak/addRecurringBreak",
  async ({ uid, daysOfWeek, startBreak, endBreak, description }, { rejectWithValue }) => {
    try {
      if (!uid || !daysOfWeek?.length || !startBreak || !endBreak) {
        throw new Error("Не все данные переданы для добавления постоянного перерыва");
      }

      const res = await apiRequest("POST", "/recurring-breaks", {
        uid,
        daysOfWeek,
        startBreak,
        endBreak,
        description: description ?? null,
      });

      return res?.data || res;
    } catch (error) {
      await handleAuthError(error);
      return rejectWithValue(getError(error, "Ошибка при добавлении постоянного перерыва"));
    }
  }
);

// Исключения (отменённые даты) постоянных перерывов мастера
export const fetchRecurringBreakExceptions = createAsyncThunk(
  "recurringBreak/fetchRecurringBreakExceptions",
  async (uid, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID не передан");

      const res = await apiRequest("GET", `/recurring-breaks/exceptions/${uid}`);
      const list = res?.data || res;
      return Array.isArray(list) ? list : [];
    } catch (error) {
      await handleAuthError(error);
      return rejectWithValue(getError(error, "Ошибка при загрузке исключений постоянных перерывов"));
    }
  }
);

export const fetchRecurringBreaks = createAsyncThunk(
  "recurringBreak/fetchRecurringBreaks",
  async (uid, { dispatch, rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID не передан");

      // Исключения грузим параллельно и ДО fulfilled — без мигания отменённого перерыва.
      // Ошибка исключений не роняет загрузку перерывов (обрабатывается своим thunk-ом).
      const [res] = await Promise.all([
        apiRequest("GET", `/recurring-breaks/${uid}`),
        dispatch(fetchRecurringBreakExceptions(uid)),
      ]);
      return res?.data || res || [];
    } catch (error) {
      await handleAuthError(error);
      return rejectWithValue(getError(error, "Ошибка при загрузке постоянных перерывов"));
    }
  }
);

export const fetchSalonRecurringBreaks = createAsyncThunk(
  "recurringBreak/fetchSalonRecurringBreaks",
  async (uids = [], { dispatch }) => {
    // fetchRecurringBreaks сам подтягивает исключения для каждого uid
    const rows = await Promise.all(
      (uids || []).map(async (uid) => {
        try {
          const items = await dispatch(fetchRecurringBreaks(uid)).unwrap();
          return {
            uid: String(uid),
            items: Array.isArray(items)
              ? items.map((item) => ({ ...item, uid: String(uid) }))
              : [],
          };
        } catch {
          return { uid: String(uid), items: [] };
        }
      })
    );

    return rows;
  }
);

export const updateRecurringBreak = createAsyncThunk(
  "recurringBreak/updateRecurringBreak",
  async (
    { uid, id, daysOfWeek, startBreak, endBreak, description, isActive },
    { rejectWithValue }
  ) => {
    try {
      if (!uid || !id) throw new Error("uid и id обязательны для обновления");

      const res = await apiRequest("PATCH", `/recurring-breaks/${uid}/${id}`, {
        daysOfWeek,
        startBreak,
        endBreak,
        description,
        isActive,
      });

      return res?.data || res;
    } catch (error) {
      await handleAuthError(error);
      return rejectWithValue(getError(error, "Ошибка при обновлении постоянного перерыва"));
    }
  }
);

export const deleteRecurringBreak = createAsyncThunk(
  "recurringBreak/deleteRecurringBreak",
  async ({ uid, id }, { rejectWithValue }) => {
    try {
      if (!uid || !id) throw new Error("uid и id обязательны для удаления");

      await apiRequest("DELETE", `/recurring-breaks/${uid}/${id}`);
      return { uid, id };
    } catch (error) {
      await handleAuthError(error);
      return rejectWithValue(getError(error, "Ошибка при удалении постоянного перерыва"));
    }
  }
);

// Отменить постоянный перерыв на одну дату
export const skipRecurringBreakForDate = createAsyncThunk(
  "recurringBreak/skipRecurringBreakForDate",
  async ({ uid, recurringBreakId, dateOfBreak }, { rejectWithValue }) => {
    try {
      if (!uid || !recurringBreakId || !dateOfBreak) {
        throw new Error("uid, recurringBreakId, dateOfBreak обязательны");
      }

      const res = await apiRequest("POST", "/recurring-breaks/exceptions", {
        uid,
        recurringBreakId,
        dateOfBreak: String(dateOfBreak).slice(0, 10),
      });

      return res?.data || res;
    } catch (error) {
      await handleAuthError(error);
      return rejectWithValue(getError(error, "Ошибка при отмене постоянного перерыва на дату"));
    }
  }
);

// Вернуть постоянный перерыв на дату (удалить исключение)
export const restoreRecurringBreakForDate = createAsyncThunk(
  "recurringBreak/restoreRecurringBreakForDate",
  async ({ uid, recurringBreakId, dateOfBreak }, { rejectWithValue }) => {
    const ymd = String(dateOfBreak || "").slice(0, 10);
    try {
      if (!uid || !recurringBreakId || !ymd) {
        throw new Error("uid, recurringBreakId, dateOfBreak обязательны");
      }

      await apiRequest("DELETE", `/recurring-breaks/exceptions/${uid}/${recurringBreakId}/${ymd}`);
      return { uid, recurringBreakId, dateOfBreak: ymd };
    } catch (error) {
      // 404 = исключения уже нет → состояние и так "восстановлено"
      if (error?.response?.status === 404) return { uid, recurringBreakId, dateOfBreak: ymd };
      await handleAuthError(error);
      return rejectWithValue(getError(error, "Ошибка при восстановлении постоянного перерыва"));
    }
  }
);

const initialState = {
  status: "idle",
  error: null,
  byUid: {},
  statusByUid: {},
  errorByUid: {},
  exceptionsByUid: {}, // { [uid]: { id, uid, recurringBreakId, dateOfBreak }[] }
};

const recurringBreakSlice = createSlice({
  name: "recurringBreak",
  initialState,
  reducers: {
    clearRecurringBreakError: (state) => {
      state.error = null;
      state.errorByUid = {};
    },
    resetRecurringBreakStatus: (state) => {
      state.status = "idle";
      state.error = null;
    },
    clearRecurringBreaks: (state) => {
      state.status = "idle";
      state.error = null;
      state.byUid = {};
      state.statusByUid = {};
      state.errorByUid = {};
      state.exceptionsByUid = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(addRecurringBreak.pending, (state, action) => {
        state.status = "loading";
        state.error = null;

        const uid = String(action.meta.arg?.uid || "");
        if (uid) {
          state.statusByUid[uid] = "loading";
          state.errorByUid[uid] = null;
        }
      })
      .addCase(addRecurringBreak.fulfilled, (state, action) => {
        state.status = "succeeded";

        const created = action.payload;
        const uid = String(created?.uid || action.meta.arg?.uid || "");

        if (uid && created) {
          if (!Array.isArray(state.byUid[uid])) state.byUid[uid] = [];
          state.byUid[uid].push({ ...created, uid });
          state.statusByUid[uid] = "succeeded";
          state.errorByUid[uid] = null;
        }
      })
      .addCase(addRecurringBreak.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error?.message;

        const uid = String(action.meta.arg?.uid || "");
        if (uid) {
          state.statusByUid[uid] = "failed";
          state.errorByUid[uid] = state.error;
        }
      })

      .addCase(fetchRecurringBreaks.pending, (state, action) => {
        const uid = String(action.meta.arg || "");

        state.status = "loading";
        state.error = null;

        if (uid) {
          state.statusByUid[uid] = "loading";
          state.errorByUid[uid] = null;
        }
      })
      .addCase(fetchRecurringBreaks.fulfilled, (state, action) => {
        state.status = "succeeded";

        const uid = String(action.meta.arg || "");
        const list = Array.isArray(action.payload) ? action.payload : [];

        if (uid) {
          state.byUid[uid] = list.map((item) => ({ ...item, uid }));
          state.statusByUid[uid] = "succeeded";
          state.errorByUid[uid] = null;
        }
      })
      .addCase(fetchRecurringBreaks.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error?.message;

        const uid = String(action.meta.arg || "");
        if (uid) {
          state.statusByUid[uid] = "failed";
          state.errorByUid[uid] = state.error;
        }
      })

      .addCase(fetchSalonRecurringBreaks.pending, (state, action) => {
        state.status = "loading";
        state.error = null;

        const uids = action.meta.arg || [];
        uids.forEach((uid) => {
          state.statusByUid[String(uid)] = "loading";
          state.errorByUid[String(uid)] = null;
        });
      })
      .addCase(fetchSalonRecurringBreaks.fulfilled, (state, action) => {
        state.status = "succeeded";

        const rows = action.payload || [];
        rows.forEach(({ uid, items }) => {
          const key = String(uid);
          state.byUid[key] = Array.isArray(items)
            ? items.map((item) => ({ ...item, uid: key }))
            : [];
          state.statusByUid[key] = "succeeded";
          state.errorByUid[key] = null;
        });
      })
      .addCase(fetchSalonRecurringBreaks.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error?.message;

        const uids = action.meta.arg || [];
        uids.forEach((uid) => {
          const key = String(uid);
          state.statusByUid[key] = "failed";
          state.errorByUid[key] = state.error;
        });
      })

      .addCase(updateRecurringBreak.pending, (state, action) => {
        state.status = "loading";
        state.error = null;

        const uid = String(action.meta.arg?.uid || "");
        if (uid) {
          state.statusByUid[uid] = "loading";
          state.errorByUid[uid] = null;
        }
      })
      .addCase(updateRecurringBreak.fulfilled, (state, action) => {
        state.status = "succeeded";

        const updated = action.payload;
        const uid = String(updated?.uid || action.meta.arg?.uid || "");

        if (uid && updated) {
          const list = state.byUid[uid] || [];
          const index = list.findIndex(
            (item) => String(item.id) === String(updated.id)
          );

          if (index !== -1) {
            list[index] = { ...updated, uid };
          } else {
            list.push({ ...updated, uid });
          }

          state.byUid[uid] = list;
          state.statusByUid[uid] = "succeeded";
          state.errorByUid[uid] = null;
        }
      })
      .addCase(updateRecurringBreak.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error?.message;

        const uid = String(action.meta.arg?.uid || "");
        if (uid) {
          state.statusByUid[uid] = "failed";
          state.errorByUid[uid] = state.error;
        }
      })

      .addCase(deleteRecurringBreak.pending, (state, action) => {
        state.status = "loading";
        state.error = null;

        const uid = String(action.meta.arg?.uid || "");
        if (uid) {
          state.statusByUid[uid] = "loading";
          state.errorByUid[uid] = null;
        }
      })
      .addCase(deleteRecurringBreak.fulfilled, (state, action) => {
        state.status = "succeeded";

        const { uid, id } = action.payload || {};
        const key = String(uid || "");

        if (key && state.byUid[key]) {
          state.byUid[key] = state.byUid[key].filter(
            (item) => String(item.id) !== String(id)
          );
          state.statusByUid[key] = "succeeded";
          state.errorByUid[key] = null;
        }
        // Бэкенд удаляет исключения вместе с правилом — синхронизируем стейт
        if (key && state.exceptionsByUid[key]) {
          state.exceptionsByUid[key] = state.exceptionsByUid[key].filter(
            (ex) => String(ex.recurringBreakId) !== String(id)
          );
        }
      })
      .addCase(deleteRecurringBreak.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error?.message;

        const uid = String(action.meta.arg?.uid || "");
        if (uid) {
          state.statusByUid[uid] = "failed";
          state.errorByUid[uid] = state.error;
        }
      })

      // ── Исключения: загрузка ──────────────────────────────────────────────
      .addCase(fetchRecurringBreakExceptions.fulfilled, (state, action) => {
        const uid = String(action.meta.arg || "");
        if (!uid) return;
        state.exceptionsByUid[uid] = (action.payload || []).map((ex) => normalizeException(ex, uid));
      })

      // ── Исключения: отменить день ─────────────────────────────────────────
      .addCase(skipRecurringBreakForDate.fulfilled, (state, action) => {
        const uid = String(action.meta.arg?.uid || "");
        if (!uid) return;
        const ex = normalizeException(action.payload, uid);
        if (!Array.isArray(state.exceptionsByUid[uid])) state.exceptionsByUid[uid] = [];
        const exists = state.exceptionsByUid[uid].some((x) =>
          isSameException(x, ex.recurringBreakId, ex.dateOfBreak)
        );
        if (!exists) state.exceptionsByUid[uid].push(ex);
      })

      // ── Исключения: вернуть день ──────────────────────────────────────────
      .addCase(restoreRecurringBreakForDate.fulfilled, (state, action) => {
        const { uid, recurringBreakId, dateOfBreak } = action.payload || {};
        const key = String(uid || "");
        if (!key || !state.exceptionsByUid[key]) return;
        state.exceptionsByUid[key] = state.exceptionsByUid[key].filter(
          (x) => !isSameException(x, recurringBreakId, dateOfBreak)
        );
      })

      .addCase(resetStore, () => initialState);
  },
});

export const {
  clearRecurringBreakError,
  resetRecurringBreakStatus,
  clearRecurringBreaks,
} = recurringBreakSlice.actions;

const EMPTY_LIST = [];

export const selectRecurringBreaksByUid = (state, uid) =>
  state.recurringBreak?.byUid?.[String(uid)] || EMPTY_LIST;

export const selectRecurringExceptionsByUid = (state, uid) =>
  state.recurringBreak?.exceptionsByUid?.[String(uid)] || EMPTY_LIST;

export const selectRecurringBreakStatusByUid = (state, uid) =>
  state.recurringBreak?.statusByUid?.[String(uid)] || "idle";

export const selectRecurringBreakErrorByUid = (state, uid) =>
  state.recurringBreak?.errorByUid?.[String(uid)] || null;

export const selectRecurringBreaksForSalon = createSelector(
  [(state) => state.recurringBreak?.byUid || {}, (_, uids) => uids || []],
  (byUid, uids = []) => {
    if (!uids.length) return [];
    return uids.flatMap((uid) => byUid[String(uid)] || []);
  }
);

export const selectAllRecurringBreaks = createSelector(
  [(state) => state.recurringBreak?.byUid || {}],
  (byUid) => Object.values(byUid || {}).flat()
);

export const selectRecurringBreakState = (state) => state.recurringBreak;
export const selectRecurringBreakStatus = (state) =>
  state.recurringBreak?.status || "idle";
export const selectRecurringBreakError = (state) =>
  state.recurringBreak?.error || null;

export default recurringBreakSlice.reducer;